import { commonStrings } from "@src/constants/common-strings";
import { MainRoutes, ProjectTabs, UsersTabs } from "@router/route-params";
import { hasUserValidFeatureCompanyLevel } from "@utils/feature-control/company/company-feature-control";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { getPrettyName } from "@utils/user-utils";
import { UtilsTypes } from "@stellar/utils";

/** Breadcrumbs can provide a callback to change their content dynamically */

/** Single object for a breadcrumb handle element. */

/** Returns the breadcrumb handle for the route "/projects" */
export function getProjectsBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      return {
        text: commonStrings.PROJECTS,
        link: `/${params.companyId}/${MainRoutes.projects}/${UtilsTypes.ProjectArchivingState.active}${data.queryParams || ""}`
      };
    }
  };
}

/** Returns the breadcrumb handle for the route "/projects/:projectStatus/:projectId" */
export function getProjectBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      const {
        companyId,
        projectId
      } = params;
      return {
        text: data?.selectedProject?.name ?? projectId ?? "",
        link: `/${companyId}/${MainRoutes.projects}/${projectId}/${ProjectTabs.overview}${data.queryParams || ""}`,
        shouldNotTranslate: true
      };
    }
  };
}

/** Returns the breadcrumb handle for the route "/members" */
export function getMembersBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      const canViewAllCompanyUsers = hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewCompanyMembers,
        featuresAvailable: data.companyContext?.featuresAvailable
      });
      return {
        text: commonStrings.MEMBERS,
        link: `/${params.companyId}/${MainRoutes.members}${data.queryParams || ""}`,
        isDisabled: !canViewAllCompanyUsers
      };
    }
  };
}

/**
 * Returns the breadcrumb handle for the route "/members/:memberId"
 */
export function getMemberProfileBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      let text;
      // If the member is the current user, use that object to react to changes for name, etc.
      if (params.memberId && params.memberId === data.currentUser?.identity) {
        text = getPrettyName(data.currentUser);
      } else {
        text = getPrettyName(data.selectedMember);
      }
      if (!text && params.memberId) {
        text = params.memberId;
      }
      return {
        text,
        shouldNotTranslate: true
      };
    }
  };
}

/** Returns the breadcrumb handle for the route "/groups" */
export function getGroupsBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      return {
        text: commonStrings.GROUPS,
        link: `/${params.companyId}/${MainRoutes.groups}${data.queryParams || ""}`
      };
    }
  };
}

/**
 * Returns the breadcrumb handle for the route "/groups/:groupId"
 */
export function getGroupBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      return {
        text: data?.selectedGroup?.name ?? params.groupId ?? "",
        shouldNotTranslate: true
      };
    }
  };
}

/** Returns the breadcrumb handle for the route "/analytics" */
export function getAnalyticsBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      return {
        text: commonStrings.ANALYTICS,
        link: `/${params.companyId}/${MainRoutes.analytics}${data.queryParams || ""}`
      };
    }
  };
}

/** Returns the breadcrumb handle for the route "/settings" */
export function getWorkspaceSettingsBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      return {
        text: commonStrings.SETTINGS,
        link: `/${params.companyId}/${MainRoutes.settings}${data.queryParams || ""}`
      };
    }
  };
}

/** Returns the breadcrumb handle for the route "/user-groups/:teamId" */
export function getTeamBreadcrumbHandle() {
  return {
    breadcrumb: (params, data) => {
      return [{
        text: `${commonStrings.TEAMS}`,
        link: `/${params.companyId}/${MainRoutes.members}/${UsersTabs.teams}${data.queryParams || ""}`
      }, {
        text: data?.selectedTeam?.name ?? params.teamId ?? "",
        link: `/${params.companyId}/${MainRoutes.teams}${data.queryParams || ""}`,
        shouldNotTranslate: true
      }];
    }
  };
}